<div class="cycle-details-container">
  <!-- Breadcrumb Navigation -->
  <nav class="breadcrumb-nav" aria-label="Breadcrumb">
    <a routerLink="/dashboard" class="breadcrumb-link">
      <mat-icon class="breadcrumb-icon">home</mat-icon>
      Dashboard
    </a>
    <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
    <a tabindex="0"
       role="button"
       class="breadcrumb-link breadcrumb-clickable"
       (click)="navigateToCycles()"
       (keyup.enter)="navigateToCycles()"
       (keyup.space)="navigateToCycles()">
      <mat-icon class="breadcrumb-icon">assignment</mat-icon>
      Cycles
    </a>
    <mat-icon class="breadcrumb-separator">chevron_right</mat-icon>
    <span class="breadcrumb-current">{{ cycleDetails.cycleName }}</span>
  </nav>

  <!-- Page Header -->
  <header class="page-header">
    <div class="header-content">
      <div class="title-section">
        <h1 class="page-title">{{ cycleDetails.cycleName }}</h1>
        <p class="page-subtitle">View and manage cycle assignments and track manager progress</p>
      </div>
    </div>
  </header>

  <!-- Cycle Summary Cards -->
  <section class="summary-section" aria-label="Cycle Summary">
    <div class="summary-cards">
      <mat-card class="summary-card market-card">
        <mat-card-content>
          <div class="card-header">
            <mat-icon class="card-icon">business</mat-icon>
            <span class="card-label">Market</span>
          </div>
          <div class="card-value">{{ cycleDetails.marketName }}</div>
        </mat-card-content>
      </mat-card>

      <mat-card class="summary-card creator-card">
        <mat-card-content>
          <div class="card-header">
            <mat-icon class="card-icon">person</mat-icon>
            <span class="card-label">Assigned By</span>
          </div>
          <div class="card-value">{{ cycleDetails.createdBy }}</div>
        </mat-card-content>
      </mat-card>

      <mat-card class="summary-card managers-card">
        <mat-card-content>
          <div class="card-header">
            <mat-icon class="card-icon">group</mat-icon>
            <span class="card-label">Assigned Managers</span>
          </div>
          <div class="card-value">{{ cycleDetails.totalManagers }}</div>
        </mat-card-content>
      </mat-card>

      <mat-card class="summary-card deadline-card">
        <mat-card-content>
          <div class="card-header">
            <mat-icon class="card-icon">schedule</mat-icon>
            <span class="card-label">Deadline</span>
          </div>
          <div class="card-value">{{ cycleDetails.deadline }}</div>
        </mat-card-content>
      </mat-card>

      <mat-card class="summary-card date-card">
        <mat-card-content>
          <div class="card-header">
            <mat-icon class="card-icon">today</mat-icon>
            <span class="card-label">Assigned On</span>
          </div>
          <div class="card-value">{{ cycleDetails.assignedAt }}</div>
        </mat-card-content>
      </mat-card>
    </div>
  </section>

  <!-- Assignments Table -->
  <section class="table-section">
    <mat-card class="table-card">
      <mat-card-header class="table-header">
        <mat-card-title>
          <mat-icon>assignment</mat-icon>
          Cycle Assignments
        </mat-card-title>
      </mat-card-header>

      <mat-card-content class="table-content">
        @if (managers.length > 0) {
          <div class="table-container">
            <table mat-table class="modern-table" matSort [dataSource]="managers">

            <!-- Manager Column -->
            <ng-container matColumnDef="userId">
              <th *matHeaderCellDef mat-header-cell mat-sort-header>Manager</th>
              <td *matCellDef="let manager" mat-cell class="manager-cell">
                <div class="manager-info">
                  <div class="app-avatar size-lg color-primary">
                    @if (manager.profileUrl) {
                    <img width="40" height="40" appDefaultImage [userName]="manager.user_display_name" [ngSrc]="manager.profileUrl" [alt]="manager.user_display_name">
                    } @else {
                      <mat-icon>account_circle</mat-icon>
                    }
                  </div>
                  <div class="manager-details">
                    <div class="manager-name-container">
                      <span class="manager-name">{{ manager.user_display_name }}</span>
                      @if (isComplianceBP && manager.pendingApprovals && manager.pendingApprovals > 0) {
                        <span class="approval-badge pending" [appBefairTooltip]="manager.pendingApprovals + (manager.pendingApprovals === 1 ? ' activity pending approval' : ' activities pending approval')">
                          <mat-icon class="approval-icon">pending_actions</mat-icon>
                          {{ manager.pendingApprovals }}
                        </span>
                      }
                    </div>
                    @if (manager.bu_unit_name) {
                      <div class="manager-bu-info">
                        <span class="bu-unit">{{ manager.bu_unit_name }}</span>
                        @if (manager.bu_role_name) {
                          <span class="bu-separator">•</span>
                          <span class="bu-role">{{ manager.bu_role_name }}</span>
                        }
                      </div>
                    }
                  </div>
                </div>
              </td>
            </ng-container>

            <!-- No Answers Column -->
            <ng-container matColumnDef="noAnswers">
              <th *matHeaderCellDef mat-header-cell mat-sort-header>No Answers</th>
              <td *matCellDef="let manager" mat-cell class="no-answers-cell">
                @if (manager.noAnswers > 0) {
                  <span class="no-answers-badge warning">
                    <mat-icon class="no-answers-icon">warning</mat-icon>
                    {{ manager.noAnswers }}
                  </span>
                } @else {
                  <span> -
                  </span>
                }
              </td>
            </ng-container>

            <!-- Deadline Column -->
            <ng-container matColumnDef="deadline">
              <th *matHeaderCellDef mat-header-cell mat-sort-header>Deadline</th>
              <td *matCellDef="let manager" mat-cell class="deadline-cell">
                <div class="deadline-info">
                  <mat-icon class="deadline-icon">schedule</mat-icon>
                  <span class="deadline-date">{{ manager.deadline }}</span>
                </div>
              </td>
            </ng-container>

            <!-- Market Column -->
            <ng-container matColumnDef="marketName">
              <th *matHeaderCellDef mat-header-cell mat-sort-header>Market</th>
              <td *matCellDef="let manager" mat-cell class="market-cell">
                <div class="market-tag">
                  <mat-icon>business</mat-icon>
                  {{ manager.marketName }}
                </div>
              </td>
            </ng-container>

            <!-- Assigned By Column -->
            <ng-container matColumnDef="assignedBy">
              <th *matHeaderCellDef mat-header-cell mat-sort-header>Assigned By</th>
              <td *matCellDef="let manager" mat-cell class="assigned-by-cell">
                {{ manager.assignedBy }}
              </td>
            </ng-container>

            <!-- Assigned Date Column -->
            <ng-container matColumnDef="assignedAt">
              <th *matHeaderCellDef mat-header-cell mat-sort-header>Assigned On</th>
              <td *matCellDef="let manager" mat-cell class="date-badge-cell">
                <span class="date-badge">
                  <mat-icon class="date-icon">event</mat-icon>
                  {{ manager.assignedAt }}
                </span>
              </td>
            </ng-container>

            <!-- Last Updated Column -->
            <ng-container matColumnDef="updatedAt">
              <th *matHeaderCellDef mat-header-cell mat-sort-header>Last Updated</th>
              <td *matCellDef="let manager" mat-cell class="date-badge-cell">
                @if (manager.updatedAt && manager.updatedAt !== '-') {
                  <span class="date-badge">
                    <mat-icon class="date-icon">update</mat-icon>
                    {{ manager.updatedAt }}
                  </span>
                } @else {
                  <span class="no-value">-</span>
                }
              </td>
            </ng-container>

            <!-- Progress Column -->
            <ng-container matColumnDef="progress">
              <th *matHeaderCellDef mat-header-cell mat-sort-header>Form Progress</th>
              <td *matCellDef="let manager" mat-cell class="progress-cell">
                <div class="progress-info">
                  <span class="progress-text">
                    {{ manager.progress }}%
                  </span>
                </div>
                <div class="progress-bar">
                  <div class="progress-fill" [ngClass]="manager.statusClass" [style.width.%]="manager.progress"></div>
                </div>
              </td>
            </ng-container>

            <!-- Table Header and Rows -->
            <tr *matHeaderRowDef="displayedColumns" mat-header-row></tr>
            <tr *matRowDef="let row; columns: displayedColumns;" mat-row
                class="table-row"
                [class.clickable-row]="true"
                (click)="onManagerRowClick(row)"></tr>
          </table>
        </div>
        } @else {
          <div class="empty-state">
            <mat-icon class="empty-icon">assignment_late</mat-icon>
            <h3>No Assignments Yet</h3>
            <p>This cycle doesn't have any manager assignments yet.</p>
          </div>
        }
      </mat-card-content>
    </mat-card>
  </section>
</div>
